'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Clock, Info } from 'lucide-react'
import { usePageRefresh, type PageRefreshState } from '../shell/pageChrome'

// A page (the design system's .main-content): the heading -- a mono eyebrow
// "TRUSTFENCE / SECTION", the h1 ending in a signal-coloured period, an
// optional (i), and on the right the timezone, the page's own controls and
// the primary action -- then an optional metrics strip (hairline top, cells
// divided by hairlines, nothing card-like), then the page's modules, then
// the mono footer. The page's refresh state goes to the topbar.

export type MetricTone = 'default' | 'signal' | 'success' | 'warning' | 'danger'
export type MetricDef = { label: ReactNode; value: ReactNode; tone?: MetricTone; note?: ReactNode; title?: string }

const METRIC_TONE: Record<MetricTone, string> = {
  default: 'text-primary',
  signal: 'text-signal',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-destructive',
}

export function Metric({ label, value, tone = 'default', note, title }: MetricDef) {
  return (
    <div title={title} className="min-w-0 px-7 first:pl-0 last:pr-0">
      <div className="font-mono text-micro uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
      <div className={`mt-2 flex items-baseline gap-3 text-metric font-normal tabular-nums tracking-[-0.015em] ${METRIC_TONE[tone]}`}>
        {value}
        {note && <span className="whitespace-nowrap font-mono text-label text-subtle">{note}</span>}
      </div>
    </div>
  )
}

export function MetricsStrip({ metrics, className = '' }: { metrics: MetricDef[]; className?: string }) {
  return (
    <div className={`mx-auto grid w-full max-w-[1240px] divide-x divide-border border-t border-border py-[22px] ${className}`} style={{ gridTemplateColumns: `repeat(${metrics.length}, minmax(0, 1fr))` }}>
      {metrics.map((m, i) => <Metric key={i} {...m} />)}
    </div>
  )
}

export function PageFrame({
  eyebrow, title, info, children, metrics, primaryAction, headerActions, timezone = 'Europe/Lisbon',
  footer, refresh, className = '', rootRef, wide = false,
}: {
  /** "Operations", "CTT · Moderation": the mono line over the title. */
  eyebrow: ReactNode
  /** The page name; the period is added. */
  title: ReactNode
  info?: ReactNode
  children?: ReactNode
  metrics?: MetricDef[]
  primaryAction?: ReactNode
  headerActions?: ReactNode
  timezone?: string | null
  footer?: ReactNode
  /** The page's refresh state, shown in the topbar. */
  refresh?: PageRefreshState
  className?: string
  rootRef?: React.Ref<HTMLDivElement>
  /** Lets the modules span the full content width (a wide table) instead of 1240 px. */
  wide?: boolean
}) {
  usePageRefresh(refresh ?? {})
  return (
    <div ref={rootRef} className={`mx-auto w-full max-w-[1840px] px-5 pb-10 pt-8 lg:px-10 ${className}`}>
      <div className="mx-auto mb-6 flex w-full max-w-[1240px] flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <span className="mb-2.5 block font-mono text-label uppercase tracking-[0.1em] text-subtle">{eyebrow}</span>
          <h1 className="flex items-center gap-2 text-h1 font-normal tracking-[-0.022em] text-foreground">
            <span>{title}<span className="text-brand-magenta">.</span></span>
            {info && <InfoPopover content={info} />}
          </h1>
        </div>
        <div className="flex flex-wrap items-center gap-5 pb-1">
          {timezone && (
            <span className="flex items-center gap-1.5 font-mono text-label text-muted-foreground"><Clock className="h-3 w-3" />{timezone}</span>
          )}
          {headerActions}
          {primaryAction}
        </div>
      </div>
      {metrics && metrics.length > 0 && <MetricsStrip metrics={metrics} className="mb-11" />}
      <div className={`mx-auto flex w-full flex-col gap-10 [counter-reset:module] ${wide ? '' : 'max-w-[1240px]'}`}>{children}</div>
      <div className="mx-auto mt-8 flex w-full max-w-[1240px] flex-wrap items-center justify-between gap-3 font-mono text-micro text-subtle">
        <span>{footer ?? (timezone ? `All times in ${timezone}.` : null)}</span>
      </div>
    </div>
  )
}

// The (i) next to a title: a popover on the card surface with a hairline.
export function InfoPopover({ content }: { content: ReactNode }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])
  return (
    <span ref={ref} className="relative inline-flex">
      <button type="button" onClick={() => setOpen((v) => !v)} title="About this page" className={`flex h-5 w-5 items-center justify-center rounded-[2px] transition-colors ${open ? 'text-signal' : 'text-subtle hover:text-signal'}`}>
        <Info className="h-3.5 w-3.5" />
      </button>
      {open && (
        <div className="absolute left-0 top-full z-30 mt-2 w-80 border border-border bg-card p-3 text-small font-normal leading-relaxed tracking-normal text-foreground shadow-notice">
          <div className="mb-1.5 font-mono text-micro uppercase tracking-widest text-subtle">Data source</div>
          {content}
        </div>
      )}
    </span>
  )
}
